import { useState } from 'react';
import dynamic from 'next/dynamic';

import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';
require('dayjs/locale/fr');
dayjs.extend(utc);
dayjs.extend(timezone);

// Wrappers
import useActiveSocialProfile from '@/hooks/user/useActiveSocialProfile';
import useUser from '@/utils/hooks/swr/useUser';
// Hooks
import useAcl from '@/utils/hooks/user/useAcl';

import { aclFeatures } from '@/lib/acl/aclFeatures';
// Lib
import { POST } from '@/lib/posts/postTypes';

import { MediaGrid } from '@/components/layouts/wrappers/MediaGrid';

// Composites
const MediaViewerModal = dynamic(
    () => import('@/components/composite/MediaViewer/MediaViewerModal')
);
const MentionMediaViewerModal = dynamic(
    () => import('@/components/pages/listening/widget/MentionMediaViewerModal')
);
const FeedViewerModal = dynamic(() => import('@/components/pages/feeds/feedModal/FeedViewerModal'));

import { PostComponent } from '@/components/base/Post/PostComponent';
import LoadMoreButton from '@/components/composite/Button/LoadMoreButton';
import { Illustration } from '@/components/composite/Illustration';

//image
import illustrationEn from './../../../../public/images/illustrations/illustrations-empty-en.webp';
import illustrationEs from './../../../../public/images/illustrations/illustrations-empty-es.webp';
import illustrationFr from './../../../../public/images/illustrations/illustrations-empty-fr.webp';

const PostsList = ({
    isLoadingInitialData,
    dataMedias,
    requestParamsMedias = {},
    configPost,
    isLoadMore,
    isLoadingMore,
    loadMoreMedia,
    isFiltersOpen,
    mutateMedias,
    handleLabelsEvent,
    handleAlbumsEvent,
    type = 'medias', // medias or stories
    remoteMedia = false,
    feedType = null,
    displayDates = true,
    isTreeStructureActivated = false,
    no_medias_placeholder = 'commons:no_posts',
    postConfig = {
        hasHover: true,
        hasFooter: true,
        showLabels: true,
        showAlbums: true
    }
}) => {
    const { hasAcl } = useAcl();
    const { t } = useTranslation('commons');
    const { activeSocialProfile } = useActiveSocialProfile();
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const [mediaViewerParams, setMediaViewerParams] = useState({
        isOpen: false,
        mediaId: null,
        mediaType: POST,
        medias: null
    });

    if (isLoadingInitialData) {
        return (
            <div
                className={clsx('w-full', {
                    'mt-[37px]': !displayDates
                })}>
                <MediaGrid>
                    <div>
                        {displayDates ? (
                            <div className="mb-3 w-2/3 animate-pulse rounded bg-icoGray-300 py-3 dark:bg-icoDarkMode-anthracite" />
                        ) : null}
                        <PostComponent
                            network={configPost?.network}
                            type={type}
                            config={configPost}
                            isLoading
                            remoteMedia
                        />
                    </div>
                    {[...Array(9).keys()].map((idx) => (
                        <div key={idx}>
                            {displayDates ? <div className="invisible mb-3 py-3" /> : null}
                            <PostComponent
                                network={configPost?.network}
                                type={type}
                                config={configPost}
                                isLoading
                                remoteMedia
                            />
                        </div>
                    ))}
                </MediaGrid>
            </div>
        );
    }

    if (!dataMedias) {
        return (
            <main
                role="alert"
                className="flex flex-1 flex-col items-center justify-center space-y-3 p-8">
                <p className="text-center">{t('errors:error_occured_fetch_data')}</p>
                <button className="text-icoBlue underline" onClick={() => mutateMedias()}>
                    {t('try_again')}
                </button>
            </main>
        );
    }

    if (dataMedias?.[0]?.[type]?.length === 0) {
        return (
            <main
                className={clsx(
                    'flex flex-1 flex-col items-center justify-center space-y-2 text-icoGray-500 dark:text-icoDarkMode-wolf',
                    {
                        'hidden sm:flex': isFiltersOpen && !isTreeStructureActivated,
                        'hidden @xl/wrapper-platform-content:flex': isTreeStructureActivated
                    }
                )}>
                <Illustration
                    src={{ en: illustrationEn, fr: illustrationFr, es: illustrationEs }}
                />

                <span>{t(no_medias_placeholder)}</span>
            </main>
        );
    }

    let lastMediaDate = null;

    return (
        <div
            className={clsx('w-full', {
                'mt-[37px]': !displayDates,
                'hidden sm:block': isFiltersOpen && !isTreeStructureActivated,
                'hidden @xl/wrapper-platform-content:block':
                    isFiltersOpen && isTreeStructureActivated
            })}>
            <MediaGrid>
                {dataMedias?.map((medias) => {
                    return medias?.[type]?.map((media, index) => {
                        const mediaDate = dayjs(media.created ?? media.published_at)
                            .tz(user?.settings.timezone)
                            .format('MM-YYYY');
                        const displayDate = mediaDate !== lastMediaDate;
                        lastMediaDate = mediaDate;

                        // Disable promoted_post if the user doesn't have the ACL
                        let promoted_post = false;
                        if (hasAcl(aclFeatures.analytic.promoted_post.read)) {
                            promoted_post = media?.promoted_post ?? false;
                        }

                        return (
                            <div key={`${media.id}-${index + 1}`}>
                                {displayDates ? (
                                    <span
                                        className={clsx(
                                            'mb-3 inline-block overflow-hidden text-ellipsis whitespace-nowrap rounded-full bg-white px-2 py-1 text-8 uppercase tracking-wider text-icoGray-500 dark:border dark:border-icoDarkMode-beton dark:bg-transparent dark:text-icoDarkMode-wolf',
                                            {
                                                invisible: !displayDate
                                            }
                                        )}>
                                        {dayjs(media.created ?? media.published_at)
                                            .tz(user?.settings.timezone)
                                            .locale(user?.settings.platform_language)
                                            .format('MMM. YYYY')}
                                    </span>
                                ) : null}
                                <PostComponent
                                    data-testid={`post-${index}`}
                                    network={configPost?.network}
                                    type={type}
                                    handleLabelsEvent={handleLabelsEvent}
                                    handleAlbumsEvent={handleAlbumsEvent}
                                    isLoading={false}
                                    data={{ ...media, promoted_post }}
                                    config={configPost}
                                    openViewer={() => {
                                        setMediaViewerParams({
                                            isOpen: true,
                                            mediaId: media?.id,
                                            mediaType: configPost?.postType,
                                            medias: medias
                                        });
                                    }}
                                    remoteMedia={remoteMedia}
                                    feedType={feedType}
                                    postConfig={postConfig}
                                />
                            </div>
                        );
                    });
                })}
            </MediaGrid>
            {isLoadMore ? (
                <div className="mt-6 flex justify-center">
                    <LoadMoreButton isLoadingMore={isLoadingMore} loadMoreItems={loadMoreMedia} />
                </div>
            ) : null}
            {feedType === 'feed' ? (
                dataMedias?.[0].type === 'tags_mentions' ? (
                    <MentionMediaViewerModal
                        isOpen={mediaViewerParams?.isOpen}
                        toggle={() => {
                            setMediaViewerParams({ ...mediaViewerParams, isOpen: false });
                        }}
                        mediaId={mediaViewerParams?.mediaId}
                        mutateMedias={mutateMedias}
                        mediaRequestParams={requestParamsMedias}
                    />
                ) : (
                    <FeedViewerModal
                        isOpen={mediaViewerParams?.isOpen}
                        toggle={() => {
                            setMediaViewerParams({ ...mediaViewerParams, isOpen: false });
                        }}
                        mediaId={mediaViewerParams?.mediaId}
                        medias={mediaViewerParams?.medias}
                        feedType={dataMedias?.[0].type ?? 'custom'}
                    />
                )
            ) : (
                <MediaViewerModal
                    //Pass the mutate function to update the medias cache based on action inside the MediaViewer
                    mutateMedias={mutateMedias}
                    isOpen={mediaViewerParams?.isOpen}
                    toggle={() => {
                        setMediaViewerParams({ ...mediaViewerParams, isOpen: false });
                    }}
                    socialProfile={activeSocialProfile}
                    mediaId={mediaViewerParams?.mediaId}
                    mediaType={mediaViewerParams?.mediaType}
                    mediaRequestParams={requestParamsMedias}
                />
            )}
        </div>
    );
};

PostsList.propTypes = {
    isLoadingInitialData: PropTypes.bool,
    dataMedias: PropTypes.array,
    requestParamsMedias: PropTypes.object,
    configPost: PropTypes.object,
    isLoadMore: PropTypes.bool,
    isLoadingMore: PropTypes.bool,
    loadMoreMedia: PropTypes.func,
    isFiltersOpen: PropTypes.bool,
    handleLabelsEvent: PropTypes.func
};

export default PostsList;
